import { CancelBtn } from 'components/Button/CancelBtn'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { NavBar } from 'components/NavBar/NavBar'
import { ISelectValue } from 'constants/globalTypes'
import { getPolicies, getSinglePolicy, upsertPolicy } from 'features/attendance/attendanceSlice'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import { Button, Flex, H1WithBack, PageFooter, PageWrapper, ColorVariant } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import {
  attendancePolicyResolverTypeOptions,
  emptyAttendancePolicyState,
  IAttendancePolicyState,
} from 'types/attendancePolicy.interface'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Alert, AlertType } from 'components/Alert/Alert'
import { CheckBox } from 'components/CheckBox/CheckBox'

export const EditPolicy = () => {
  const [state, setState] = useState<IAttendancePolicyState>(emptyAttendancePolicyState())
  const { singlePolicy, policies } = useSelector((store: RootState) => store.attendance)
  const { id } = useParams()
  const [selectedRole, setSelectedRole] = useState<ISelectValue | null>(null)
  const [selectedUser, setSelectedUser] = useState<ISelectValue | null>(null)
  const [pickAdmins, setPickAdmins] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    if (id) {
      dispatch(getSinglePolicy(id))
      return
    }
    dispatch(getPolicies({ page: 1, limit: 1 })).then((resp: any) => {
      const existingId = resp?.payload?.data?.[0]?.id
      if (existingId) {
        dispatch(getSinglePolicy(existingId))
      }
    })
  }, [dispatch, id])

  useEffect(() => {
    if (singlePolicy) {
      setState(singlePolicy)
    }
  }, [singlePolicy])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleWorkflowStepChange = (e: React.ChangeEvent<HTMLInputElement>, stepId: number) => {
    const { value, name } = e.target
    setState((prev) => {
      const workflowSteps = [...prev.workflowSteps]
      const stepIndex = workflowSteps.findIndex((s) => s.id === stepId)
      if (stepIndex === -1) return prev
      workflowSteps[stepIndex] = { ...workflowSteps[stepIndex], [name]: value }
      return { ...prev, workflowSteps }
    })
  }

  const handleWorkflowStepSelect = (e: ISelectValue, stepId: number) => {
    setState((prev) => {
      const workflowSteps = [...prev.workflowSteps]
      const stepIndex = workflowSteps.findIndex((s) => s.id === stepId)
      if (stepIndex === -1) return prev
      workflowSteps[stepIndex] = {
        ...workflowSteps[stepIndex],
        resolverType: e,
        roles: e.value === 1 ? workflowSteps[stepIndex].roles : [],
        users: e.value === 2 ? workflowSteps[stepIndex].users : [],
      }
      return { ...prev, workflowSteps }
    })
  }

  const handleAddUser = (stepId: number) => {
    if (!selectedUser) return
    setState((prev) => {
      const workflowSteps = [...prev.workflowSteps]
      const stepIndex = workflowSteps.findIndex((s) => s.id === stepId)
      if (stepIndex === -1) return prev
      const users = workflowSteps[stepIndex].users || []
      if (users.some((u) => u.value === selectedUser.value)) return prev
      workflowSteps[stepIndex] = { ...workflowSteps[stepIndex], users: [...users, selectedUser] }
      return { ...prev, workflowSteps }
    })
    setSelectedUser(null)
  }

  const handleAddRole = (stepId: number) => {
    if (!selectedRole) return
    setState((prev) => {
      const workflowSteps = [...prev.workflowSteps]
      const stepIndex = workflowSteps.findIndex((s) => s.id === stepId)
      if (stepIndex === -1) return prev
      const roles = workflowSteps[stepIndex].roles || []
      if (roles.some((r) => r.value === selectedRole.value)) return prev
      workflowSteps[stepIndex] = { ...workflowSteps[stepIndex], roles: [...roles, selectedRole] }
      return { ...prev, workflowSteps }
    })
    setSelectedRole(null)
  }

  const handleRemoveUser = (stepId: number, userId: number) => {
    setState((prev) => ({
      ...prev,
      workflowSteps: prev.workflowSteps.map((s) =>
        s.id === stepId ? { ...s, users: s.users.filter((u) => u.value !== userId) } : s,
      ),
    }))
  }

  const handleRemoveRole = (stepId: number, roleId: number) => {
    setState((prev) => ({
      ...prev,
      workflowSteps: prev.workflowSteps.map((s) =>
        s.id === stepId ? { ...s, roles: s.roles.filter((r) => r.value !== roleId) } : s,
      ),
    }))
  }

  const handleRemoveStep = (stepId: number) => {
    setState((prev) => {
      if (prev.workflowSteps.length <= 1) return prev
      const workflowSteps = prev.workflowSteps
        .filter((s) => s.id !== stepId)
        .map((s, index) => ({ ...s, order: index + 1 }))
      return { ...prev, workflowSteps }
    })
  }

  const parseRoles = (data: any) => data.data.map((item: any) => ({ label: item.name, value: item.id }))

  const handleAddStep = () => {
    setState((prev) => ({
      ...prev,
      workflowSteps: [
        ...prev.workflowSteps,
        {
          id: Date.now(),
          name: '',
          order: prev.workflowSteps.length + 1,
          resolverType: { label: 'Role', value: 1 },
          roles: [],
          users: [],
        },
      ],
    }))
  }

  const handleSubmit = async () => {
    const resp = await dispatch(upsertPolicy(state))
    if (resp.meta.requestStatus === 'fulfilled') {
      navigate('/hr/attendance/policies')
    }
  }

  const hasExisting = Boolean(state.id || id || policies?.data?.length)

  return (
    <>
      <NavBar />
      <PageWrapper>
        <H1WithBack arrow title={hasExisting ? 'Edit Approval Policy' : 'Set Up Approval Policy'} />
        <Flex mt='16' mb='16'>
          <Alert
            title='Changes apply to new reports only'
            description='Editing this policy does not change or reset approval progress on reports that already exist.'
            type={AlertType.INFO}
            colorVariant={ColorVariant.PRIMARY}
          />
        </Flex>
        <Flex width='100%' between mt='16' gap='5rem'>
          <Flex column gap='1rem' width='100%'>
            <Input
              placeholder='General Policy'
              labelText='Name'
              name='name'
              nomarg
              value={state.name}
              onChange={handleChange}
              required
            />
            <Input
              placeholder='Policy Description'
              labelText='Description'
              name='description'
              nomarg
              value={state.description}
              onChange={handleChange}
              required
            />
          </Flex>
          <Flex column gap='1rem' width='100%'>
            <TitleText weight='bold'>Workflow Steps</TitleText>
            {state.workflowSteps.map((s) => (
              <Flex key={s.id || s.order} column gap='0.5rem'>
                <Flex between center width='100%'>
                  <OtherText grey>{`Step ${s.order}`}</OtherText>
                  {state.workflowSteps.length > 1 && (
                    <CloseIcon onClick={() => handleRemoveStep(s.id)} />
                  )}
                </Flex>
                <Input
                  labelText='Name'
                  placeholder='First step...'
                  name='name'
                  value={s.name}
                  onChange={(e) => handleWorkflowStepChange(e, s.id)}
                  required
                />
                <HonestSelectInput
                  labelText='Resolver Type'
                  options={attendancePolicyResolverTypeOptions}
                  name='resolverType'
                  defaultValue={s.resolverType?.value}
                  onChange={(e: ISelectValue) => handleWorkflowStepSelect(e, s.id)}
                />
                {s.resolverType?.value === 2 ? (
                  <>
                    <CheckBox
                      state={pickAdmins}
                      onClick={() => {
                        setPickAdmins((prev) => !prev)
                        setSelectedUser(null)
                      }}
                      square
                      title='Pick from company administrators'
                    />
                    <Flex gap='1rem' alignEnd>
                      {pickAdmins ? (
                        <HonestSelectAsyncInput
                          keyProp={1}
                          nomarg
                          link='users'
                          additionalParams={{ accessGroup: 'company_administrator' }}
                          name='employee'
                          labelText='Company Administrator'
                          onChange={(v) => setSelectedUser({ ...v })}
                          parserFunction={(data: any) =>
                            data.data.map((i: any) => ({
                              label: i.fullName || `${i.firstName} ${i.lastName}`,
                              value: i.id,
                            }))
                          }
                        />
                      ) : (
                        <HonestSelectAsyncInput
                          keyProp={2}
                          nomarg
                          link='users/basic'
                          name='employee'
                          labelText='Employee'
                          onChange={(v) => setSelectedUser({ ...v })}
                          parserFunction={(data: any) =>
                            data.data.map((i: any) => ({
                              label: i.fullName,
                              profilePictureUrl: i.getImagePresignedUrl,
                              value: i.id,
                            }))
                          }
                        />
                      )}
                      <Button orange onClick={() => handleAddUser(s.id)}>
                        Add
                      </Button>
                    </Flex>
                    <Flex column gap='0.5rem'>
                      <TitleText>Employees</TitleText>
                      {s.users.length ? (
                        s.users.map((u) => (
                          <Flex key={u.value} between center>
                            <BodyText>{u.label}</BodyText>
                            <CloseIcon onClick={() => handleRemoveUser(s.id, u.value)} />
                          </Flex>
                        ))
                      ) : (
                        <OtherText grey>No employees added</OtherText>
                      )}
                    </Flex>
                  </>
                ) : (
                  <>
                    <Flex gap='1rem' alignEnd>
                      <HonestSelectAsyncInput
                        link='roles'
                        placeholder='Search role'
                        name='roleId'
                        labelText='Role'
                        onChange={(v) => setSelectedRole({ ...v })}
                        parserFunction={parseRoles}
                        nomarg
                      />
                      <Button orange onClick={() => handleAddRole(s.id)}>
                        Add
                      </Button>
                    </Flex>
                    <Flex column gap='0.5rem'>
                      <TitleText>Roles</TitleText>
                      {s.roles.length ? (
                        s.roles.map((r) => (
                          <Flex key={r.value} between center>
                            <BodyText>{r.label}</BodyText>
                            <CloseIcon onClick={() => handleRemoveRole(s.id, r.value)} />
                          </Flex>
                        ))
                      ) : (
                        <OtherText grey>No roles added</OtherText>
                      )}
                    </Flex>
                  </>
                )}
              </Flex>
            ))}
            <Button orange onClick={handleAddStep}>
              Add Step
            </Button>
          </Flex>
        </Flex>
        <PageFooter>
          <Flex width='100%' end='true'>
            <CancelBtn />
            <Button ml='15' height='40' orange onClick={handleSubmit}>
              Save
            </Button>
          </Flex>
        </PageFooter>
      </PageWrapper>
    </>
  )
}
